import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import Loading from '../../components/common/Loading/Loading';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Pagination from '../../components/common/Pagination/Pagination';
import DataTable from '../../components/common/DataTable/DataTable';
import Button from '../../components/common/Button/Button';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import FiltersGroup from '../../components/common/FiltersGroup/FiltersGroup';
import ToolbarRow from '../../components/common/ToolbarRow/ToolbarRow';
import PageContainer, { PageBody } from '../../components/common/PageContainer/PageContainer';
import FilterChipsBar from '../../components/common/FilterChipsBar/FilterChipsBar';
import type { FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import { useAuth } from '../../contexts/AuthContext';
import { usePageSize } from '../../hooks/usePageSize';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import useExercises from '../../hooks/useExercises';
import type { ExerciseItem, ExerciseType } from '../../types/exercise';
import { canManageExercises } from '../../utils/permissions';
import ExerciseListRow from './components/ExerciseListRow/ExerciseListRow';
import styles from './Exercises.module.css';

const EXERCISE_TYPE_OPTIONS: Array<{ value: ExerciseType; label: string }> = [
  { value: 'strength', label: 'Força' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'flexibility', label: 'Flexibilidade' },
];

const EXERCISES_QUERY_SCHEMA = {
  page: queryFilter.integer(1, { min: 1 }),
  search: queryFilter.string(),
  groups: queryFilter.array(),
  exerciseTypes: queryFilter.array<ExerciseType>([], {
    allowed: EXERCISE_TYPE_OPTIONS.map((option) => option.value),
  }),
  resistanceTypeIds: queryFilter.array(),
};

const Exercises: React.FC = () => {
  const navigate = useNavigate();
  const { roles } = useAuth();
  const canManage = canManageExercises(roles);
  const [deleteTarget, setDeleteTarget] = useState<ExerciseItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { filters, setFilter, setFilters } = useQueryFilters(
    EXERCISES_QUERY_SCHEMA,
    { pageKey: 'page' },
  );
  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('exercises_page_size');
  const {
    search: searchTerm,
    groups: selectedGroups,
    exerciseTypes: selectedExerciseTypes,
    resistanceTypeIds: selectedResistanceTypeIds,
  } = filters;

  const {
    exercises,
    pagination,
    loading,
    error,
    totalExercises,
    groupOptions,
    resistanceTypeOptions,
    refreshExercises,
  } = useExercises(page, pageSize, {
    searchTerm,
    groups: selectedGroups,
    exerciseTypes: selectedExerciseTypes,
    resistanceTypeIds: selectedResistanceTypeIds,
  });

  const hasActiveFilters = searchTerm.trim().length > 0
    || selectedGroups.length > 0
    || selectedExerciseTypes.length > 0
    || selectedResistanceTypeIds.length > 0;

  const handleClearFilters = useCallback(() => {
    setFilters({
      search: '',
      groups: [],
      exerciseTypes: [],
      resistanceTypeIds: [],
    });
  }, [setFilters]);

  const handleRemoveGroupChip = useCallback((groupValue: string) => {
    setFilter('groups', (currentValues) => currentValues.filter((value) => value !== groupValue));
  }, [setFilter]);

  const handleRemoveExerciseTypeChip = useCallback((exerciseTypeValue: string) => {
    setFilter('exerciseTypes', (currentValues) => currentValues.filter((value) => value !== exerciseTypeValue));
  }, [setFilter]);

  const handleRemoveResistanceTypeChip = useCallback((resistanceTypeValue: string) => {
    setFilter('resistanceTypeIds', (currentValues) => currentValues.filter((value) => value !== resistanceTypeValue));
  }, [setFilter]);

  const filterChips: FilterChipEntry[] = useMemo(() => {
    const groupChips = selectedGroups.map((groupValue) => {
      const groupOption = groupOptions.find((option) => option.value === groupValue);

      return {
        filterKey: 'groups',
        value: groupValue,
        label: groupOption?.label ?? groupValue,
        onRemove: handleRemoveGroupChip,
      };
    });

    const exerciseTypeChips = selectedExerciseTypes.map((exerciseTypeValue) => {
      const exerciseTypeOption = EXERCISE_TYPE_OPTIONS.find((option) => option.value === exerciseTypeValue);

      return {
        filterKey: 'exerciseType',
        value: exerciseTypeValue,
        label: exerciseTypeOption?.label ?? exerciseTypeValue,
        onRemove: handleRemoveExerciseTypeChip,
      };
    });

    const resistanceTypeChips = selectedResistanceTypeIds.map((resistanceTypeValue) => {
      const resistanceTypeOption = resistanceTypeOptions.find((option) => option.value === resistanceTypeValue);

      return {
        filterKey: 'resistanceTypeId',
        value: resistanceTypeValue,
        label: resistanceTypeOption?.label ?? resistanceTypeValue,
        onRemove: handleRemoveResistanceTypeChip,
      };
    });

    return [
      ...groupChips,
      ...exerciseTypeChips,
      ...resistanceTypeChips,
    ];
  }, [groupOptions, handleRemoveExerciseTypeChip, handleRemoveGroupChip, handleRemoveResistanceTypeChip, resistanceTypeOptions, selectedExerciseTypes, selectedGroups, selectedResistanceTypeIds]);

  const handleCreateExercise = useCallback(() => {
    navigate('/exercicios/novo');
  }, [navigate]);

  const handleOpenVideo = useCallback((exercise: ExerciseItem) => {
    window.open(exercise.videoUrl, '_blank', 'noopener');
  }, []);

  const handleViewExercise = useCallback((exercise: ExerciseItem) => {
    navigate(`/exercicios/${exercise.id}/editar`);
  }, [navigate]);

  const handleEditExercise = useCallback((exercise: ExerciseItem) => {
    navigate(`/exercicios/${exercise.id}/editar`);
  }, [navigate]);

  const handleDuplicateExercise = useCallback((exercise: ExerciseItem) => {
    window.alert(`Duplicação de "${exercise.name}" em implementação.`);
  }, []);

  const handleDeleteExercise = useCallback((exercise: ExerciseItem) => {
    setDeleteTarget(exercise);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      setIsDeleting(true);
      const { ExerciseService } = await import('../../services/ExerciseService');
      await ExerciseService.deleteExercise(deleteTarget.id);
      await refreshExercises();
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, refreshExercises]);

  const buildMenuItems = useCallback((exercise: ExerciseItem) => (canManage ? [
    { label: 'Editar', onClick: () => handleEditExercise(exercise) },
    { label: 'Duplicar', onClick: () => handleDuplicateExercise(exercise) },
    { label: 'Inativar', onClick: () => handleDeleteExercise(exercise), variant: 'danger' as const },
  ] : []), [canManage, handleDeleteExercise, handleDuplicateExercise, handleEditExercise]);

  return (
    <PageContainer>
      <ToolbarRow className={styles['toolbar-row']}>
        {canManage && (
          <Button
            variant="primary"
            size="small"
            className={styles['create-button']}
            onClick={handleCreateExercise}
          >
            Novo exercício
          </Button>
        )}

        <RecordCount label="Exercícios cadastrados:" count={totalExercises} />

        <FiltersGroup className={styles['filters-group']}>
          <SearchFilterBar
            searchValue={searchTerm}
            onSearchChange={(value) => setFilter('search', value)}
            searchPlaceholder="Pesquisar"
            loading={loading}
          />

          <MultiSelectFilter
            values={selectedGroups}
            onChange={(values) => setFilter('groups', values)}
            options={groupOptions}
            placeholder="Grupo muscular"
            clearButtonLabel="Limpar filtro"
            hideChips
            showSelectionCount={false}
            className={styles['group-filter']}
          />

          <MultiSelectFilter
            values={selectedExerciseTypes}
            onChange={(values) => setFilter('exerciseTypes', values as ExerciseType[])}
            options={EXERCISE_TYPE_OPTIONS}
            placeholder="Tipo de exercício"
            clearButtonLabel="Limpar filtro"
            wrapperClassName={styles['single-filter']}
            hideChips
            showSelectionCount={false}
          />

          <MultiSelectFilter
            values={selectedResistanceTypeIds}
            onChange={(values) => setFilter('resistanceTypeIds', values)}
            options={resistanceTypeOptions}
            placeholder="Tipo de resistência"
            clearButtonLabel="Limpar filtro"
            wrapperClassName={styles['single-filter']}
            hideChips
            showSelectionCount={false}
          />

          <Button
            variant="ghost"
            size="small"
            icon="filter_alt"
            className={styles['filter-button']}
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            aria-label="Limpar filtros"
            title={hasActiveFilters ? 'Limpar filtros' : 'Nenhum filtro ativo'}
          />
        </FiltersGroup>
      </ToolbarRow>

      <div className={styles['chips-row']}>
        <FilterChipsBar chips={filterChips} />
      </div>

      <PageBody>
        {error ? (
          <EmptyState message={`Erro: ${error}`} />
        ) : loading ? (
          <Loading />
        ) : exercises.length === 0 ? (
          <EmptyState message="Nenhum exercício encontrado com os filtros selecionados." />
        ) : (
          <DataTable
            headers={['Exercício', 'Grupo', 'Tipo de resistência', 'Objetivo', 'URL', 'Criado em', 'Ações']}
            gridTemplate="320px 120px 180px 1fr 120px 130px 120px"
            highlightTerm={searchTerm}
          >
            {exercises.map((exercise) => (
              <ExerciseListRow
                key={exercise.id}
                exercise={exercise}
                onView={handleViewExercise}
                onOpenVideo={handleOpenVideo}
                menuItems={buildMenuItems(exercise)}
              />
            ))}
          </DataTable>
        )}
      </PageBody>

      {exercises.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        open={canManage && Boolean(deleteTarget)}
        title="Inativar exercício"
        message={`Tem certeza que deseja inativar ${deleteTarget?.name ?? 'este exercício'}?`}
        confirmLabel={isDeleting ? 'Inativando...' : 'Inativar'}
        variant="danger"
        loading={isDeleting}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteTarget(null);
          }
        }}
        onConfirm={handleDeleteConfirm}
      />
    </PageContainer>
  );
};

export default Exercises;